import { z } from 'zod';
import { APP_URL_SCHEME } from './app-client.js';

/**
 * Microsoft 365, Oppy's first built-in connection ("Hazır bağlantı", Phase 15e,
 * D-191…): Outlook mail and calendar through Microsoft Graph, with each person's
 * own delegated sign-in against the firm's own Entra app registration. The tools
 * run inside Oppy (worker + gateway); the model calls them like connection tools
 * (`mcp__microsoft365__<tool>`). Part 3 (D-220…, SA-112…, DOC-017…) adds four
 * write tools that only ever run after the person's click on a locked card
 * (microsoft365-write.ts) and documents copied from OneDrive, SharePoint and
 * Outlook attachments (microsoft365-files.ts).
 */

/** The built-in connection's kind, its tool prefix and the `builtin_connections.kind` value. */
export const MICROSOFT365 = 'microsoft365' as const;

/** Stable id of the built-in connection: name-based UUID of "dbrain:builtin-connection:microsoft365" (sha1, v5 layout). */
export const MICROSOFT365_ID = '4cbbb2ac-4b7a-5cfd-810b-92df0fb95da4';

export const MICROSOFT365_DISPLAY_NAME = 'Microsoft 365';

/** The read tools of part 2 (read-only; they run without a card on a trusted built-in, D-179). */
export const MICROSOFT365_READ_TOOLS = ['mail_ara', 'mail_oku', 'eki_oku', 'takvime_bak', 'toplanti_ayrintisi', 'uygun_saat_bul'] as const;
export type Microsoft365ReadTool = (typeof MICROSOFT365_READ_TOOLS)[number];

/**
 * The write tools of part 3: always asked on a locked card in every mode, never for helpers; nothing is written
 * before the person's click, and only the API's call to the gateway writes (D-220…).
 */
export const MICROSOFT365_WRITE_TOOLS = ['yanit_taslagi', 'taslagi_gonder', 'mail_gonder', 'toplanti_olustur'] as const;
export type Microsoft365WriteTool = (typeof MICROSOFT365_WRITE_TOOLS)[number];

/** Every tool of the built-in connection: the read tools, then the write tools. */
export const MICROSOFT365_TOOLS = [...MICROSOFT365_READ_TOOLS, ...MICROSOFT365_WRITE_TOOLS] as const;
export type Microsoft365Tool = (typeof MICROSOFT365_TOOLS)[number];

export const isMicrosoft365WriteTool = (tool: string | null | undefined): tool is Microsoft365WriteTool =>
  typeof tool === 'string' && (MICROSOFT365_WRITE_TOOLS as readonly string[]).includes(tool);

/** The model-facing name, e.g. mcp__microsoft365__mail_ara. */
export const microsoft365ToolName = (tool: Microsoft365Tool): string => `mcp__${MICROSOFT365}__${tool}`;

export function microsoft365ToolOf(name: string): Microsoft365Tool | null {
  const prefix = `mcp__${MICROSOFT365}__`;
  if (!name.startsWith(prefix)) return null;
  const tool = name.slice(prefix.length);
  return (MICROSOFT365_TOOLS as readonly string[]).includes(tool) ? (tool as Microsoft365Tool) : null;
}

/** Which switch of "Neler açık" a tool needs. */
export const MICROSOFT365_TOOL_FEATURE: Record<Microsoft365Tool, Microsoft365Feature> = {
  mail_ara: 'mail_read',
  mail_oku: 'mail_read',
  eki_oku: 'mail_read',
  takvime_bak: 'calendar_read',
  toplanti_ayrintisi: 'calendar_read',
  uygun_saat_bul: 'calendar_read',
  yanit_taslagi: 'mail_send',
  taslagi_gonder: 'mail_send',
  mail_gonder: 'mail_send',
  toplanti_olustur: 'calendar_write',
};

/**
 * The switches of "Neler açık" (MsYonetimEntegrasyon), one per firm; the four of part 3 start off (migration 0023).
 * Outlook attachments (the picker's "Outlook ekleri", "Belgelerime kaydet") follow mail_read.
 */
export const microsoft365Feature = z.enum(['mail_read', 'calendar_read', 'mail_send', 'calendar_write', 'onedrive_import', 'sharepoint_import']);
export type Microsoft365Feature = z.infer<typeof microsoft365Feature>;
export const MICROSOFT365_FEATURES: readonly Microsoft365Feature[] = microsoft365Feature.options;

/** The read switches of part 2. */
export const MICROSOFT365_READ_FEATURES = ['mail_read', 'calendar_read'] as const satisfies readonly Microsoft365Feature[];

/**
 * A switch that needs another one on: turning the read switch off turns its write switch off in the same change
 * (the database refuses a write switch on while its read switch is off, migration 0023).
 */
export const MICROSOFT365_FEATURE_REQUIRES: Partial<Record<Microsoft365Feature, Microsoft365Feature>> = {
  mail_send: 'mail_read',
  calendar_write: 'calendar_read',
};

/** The settings body's field of each switch (PATCH /api/admin/microsoft365, the `microsoft.settings_changed` rows). */
export const MICROSOFT365_FEATURE_FIELDS = {
  mail_read: 'mailRead',
  calendar_read: 'calendarRead',
  mail_send: 'mailSend',
  calendar_write: 'calendarWrite',
  onedrive_import: 'onedriveImport',
  sharepoint_import: 'sharepointImport',
} as const satisfies Record<Microsoft365Feature, string>;

/**
 * Delegated scopes of part 2: read only. getSchedule (free/busy) works with Calendars.Read.
 * Part 3 asks for more scopes incrementally, per feature and only when it is first used (MICROSOFT365_FEATURE_SCOPES).
 */
export const MICROSOFT365_READ_SCOPES = ['openid', 'profile', 'offline_access', 'User.Read', 'Mail.Read', 'Calendars.Read'] as const;

/**
 * The Graph permissions each feature needs. Server-side only: a client names features (the connect body), never
 * scope strings. The sign-in asks the read scopes plus the scopes of the features asked for (microsoft365SignInScopes).
 */
export const MICROSOFT365_FEATURE_SCOPES: Record<Microsoft365Feature, readonly string[]> = {
  mail_read: ['Mail.Read'],
  calendar_read: ['Calendars.Read'],
  mail_send: ['Mail.ReadWrite', 'Mail.Send'],
  calendar_write: ['Calendars.ReadWrite'],
  onedrive_import: ['Files.Read.All'],
  sharepoint_import: ['Files.Read.All', 'Sites.Read.All'],
};

/** A granted scope that also grants another one (lower case): read-write covers read, all files cover one's own. */
const SCOPE_IMPLIES: Record<string, readonly string[]> = {
  'mail.readwrite': ['mail.read'],
  'calendars.readwrite': ['calendars.read'],
  'files.read.all': ['files.read'],
};

/** A Graph scope as compared: without Graph's resource prefix, lower case ("https://graph.microsoft.com/Mail.Read" → "mail.read"). */
export function microsoft365ScopeKey(scope: string): string {
  return scope
    .trim()
    .replace(/^https:\/\/graph\.microsoft\.com\//i, '')
    .toLowerCase();
}

/** The granted scopes (as Entra returned them) cover every scope the feature needs. */
export function hasFeatureScopes(granted: readonly string[], feature: Microsoft365Feature): boolean {
  const have = new Set<string>();
  for (const scope of granted) {
    const key = microsoft365ScopeKey(scope);
    if (!key) continue;
    have.add(key);
    for (const implied of SCOPE_IMPLIES[key] ?? []) have.add(implied);
  }
  return MICROSOFT365_FEATURE_SCOPES[feature].every((scope) => have.has(microsoft365ScopeKey(scope)));
}

/** Per feature: the granted scopes cover it (Microsoft365StatusDto.grants). */
export function microsoft365Grants(granted: readonly string[]): Record<Microsoft365Feature, boolean> {
  return Object.fromEntries(MICROSOFT365_FEATURES.map((feature) => [feature, hasFeatureScopes(granted, feature)])) as Record<Microsoft365Feature, boolean>;
}

/** What a sign-in asks: the read scopes, then each named feature's scopes, once each (compared without case or prefix). */
export function microsoft365SignInScopes(features: readonly Microsoft365Feature[] = []): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const scope of [...MICROSOFT365_READ_SCOPES, ...features.flatMap((feature) => MICROSOFT365_FEATURE_SCOPES[feature])]) {
    const key = microsoft365ScopeKey(scope);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(scope);
  }
  return out;
}

/** A Graph id as Oppy passes it on (immutable ids are base64url-like): nothing that could change a path. */
export const microsoft365GraphId = z.string().min(1).max(512).regex(/^[A-Za-z0-9+/=_-]+$/);

/** Microsoft's fixed tenant id of personal accounts (outlook.com, hotmail.com): always refused. */
export const MICROSOFT_PERSONAL_TENANT_ID = '9188040d-6c67-4c5b-b112-36a304b66dad';

export const MICROSOFT_LOGIN_URL = 'https://login.microsoftonline.com';
export const MICROSOFT_GRAPH_URL = 'https://graph.microsoft.com';

/** The redirect URI the admin adds to the Entra registration: on the chat address, never from Host headers. */
export function microsoft365RedirectUri(appOrigin: string, override?: string | null): string {
  if (override) return override;
  return `${appOrigin.replace(/\/+$/, '')}/api/oauth/callback`;
}

/** Entra accepts https redirect URIs, and http only for loopback (localhost, 127.0.0.1). */
export function redirectUriAccepted(uri: string): boolean {
  try {
    const url = new URL(uri);
    if (url.protocol === 'https:') return true;
    return url.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(url.hostname);
  } catch {
    return false;
  }
}

const guid = z
  .string()
  .trim()
  .regex(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/, 'a GUID like 00000000-0000-0000-0000-000000000000')
  .transform((value) => value.toLowerCase());

export const microsoft365SignInMethod = z.enum(['auth_code', 'device_code']);
export type Microsoft365SignInMethod = z.infer<typeof microsoft365SignInMethod>;

export const microsoft365Availability = z.enum(['everyone', 'teams']);
export type Microsoft365Availability = z.infer<typeof microsoft365Availability>;

// ---- admin (Yönetim › Entegrasyonlar › Microsoft 365) -----------------------------------------

/** Step 2 of the setup: written by the super admin; the secret is write-only (never sent back). */
export const microsoft365SetupBody = z.object({
  entraTenantId: guid,
  clientId: guid,
  /** only when typed: empty keeps the saved secret */
  clientSecret: z.string().min(1).max(4096).optional(),
  signInMethod: microsoft365SignInMethod.default('auth_code'),
  /** optional: Microsoft shows it when the secret is made; the summary warns before it */
  secretExpiresOn: z.iso.date().nullable().optional(),
});
export type Microsoft365SetupBody = z.infer<typeof microsoft365SetupBody>;

/** The summary's switches; each change is audited on its own. */
export const microsoft365SettingsBody = z
  .object({
    enabled: z.boolean().optional(),
    availability: microsoft365Availability.optional(),
    /** with availability 'teams': the teams that may use it (teams.id) */
    teamIds: z.array(z.uuid()).max(500).optional(),
    mailRead: z.boolean().optional(),
    calendarRead: z.boolean().optional(),
    /** part 3, default off: "Mail gönderme" (needs mailRead on) and "Toplantı oluşturma" (needs calendarRead on) */
    mailSend: z.boolean().optional(),
    calendarWrite: z.boolean().optional(),
    /** part 3, default off: "OneDrive'dan belge getirme", "SharePoint'ten belge getirme" */
    onedriveImport: z.boolean().optional(),
    sharepointImport: z.boolean().optional(),
    /** the Microsoft account must have the person's Oppy e-mail address (default on; D-194) */
    matchAccount: z.boolean().optional(),
  })
  .refine((body) => Object.values(body).some((v) => v !== undefined), 'nothing to update');
export type Microsoft365SettingsBody = z.infer<typeof microsoft365SettingsBody>;

/** A team admin's switch for their own team, within the firm's limit. */
export const microsoft365TeamBody = z.object({ enabled: z.boolean() });

export type Microsoft365TestCode =
  | 'tenant_not_found'
  | 'app_not_found'
  | 'bad_secret'
  | 'secret_expired'
  | 'no_secret'
  | 'public_client_disabled'
  | 'redirect_not_https'
  | 'unreachable'
  | 'unexpected';

export interface Microsoft365TestResultDto {
  ok: boolean;
  /** why it failed; the screen explains it in plain Turkish */
  code?: Microsoft365TestCode;
  /** the HTTP status Microsoft answered with, when it answered */
  status?: number;
  latencyMs: number;
  testedAt: string;
}

export interface Microsoft365TeamDto {
  id: string;
  name: string;
  /** with availability 'teams': chosen by the super admin */
  allowed: boolean;
  /** the team admin's switch (on unless a team admin turned it off) */
  teamEnabled: boolean;
  /** people of the team with a connected Microsoft account / all people of the team */
  connected: number;
  members: number;
  changedAt: string | null;
}

export interface Microsoft365AdminDto {
  /** tenant id, client id and (for auth_code) a secret are saved */
  configured: boolean;
  enabled: boolean;
  availability: Microsoft365Availability;
  features: Record<Microsoft365Feature, boolean>;
  /** a person may connect only the Microsoft account with their own Oppy e-mail address (default on) */
  matchAccount: boolean;
  entraTenantId: string | null;
  clientId: string | null;
  hasSecret: boolean;
  secretExpiresOn: string | null;
  signInMethod: Microsoft365SignInMethod;
  /** what the admin adds to the Entra registration, computed from APP_ORIGIN */
  redirectUri: string;
  /** false on plain-http installs: only "Kodla" (device code) works there */
  redirectUriAccepted: boolean;
  connectedCount: number;
  teams: Microsoft365TeamDto[];
  lastTest: Microsoft365TestResultDto | null;
  updatedAt: string | null;
}

/** What a team admin sees: the firm's state and their own teams, never the Microsoft settings. */
export interface Microsoft365TeamAdminDto {
  configured: boolean;
  enabled: boolean;
  availability: Microsoft365Availability;
  features: Record<Microsoft365Feature, boolean>;
  teams: Microsoft365TeamDto[];
}

// ---- the person (Bağlantılar, Ayarlar › Hesap › Bağlı hesaplar, chat notices) ----------------

/**
 * One state per person, in this order of precedence:
 * unsupported: a personal space (no firm); not_configured: the firm has not set it up;
 * off_firm / not_allowed / off_team: the firm turned it off, the person is not in a chosen team,
 * or a team admin turned it off for their team; not_connected; needs_reconnect (Microsoft no
 * longer accepts the sign-in); consent_required (the tenant's admin must consent); connected.
 */
export type Microsoft365State =
  | 'unsupported'
  | 'not_configured'
  | 'off_firm'
  | 'not_allowed'
  | 'off_team'
  | 'not_connected'
  | 'needs_reconnect'
  | 'consent_required'
  | 'connected';

export interface Microsoft365AccountDto {
  email: string;
  name: string | null;
  connectedAt: string;
  lastUsedAt: string | null;
}

export interface Microsoft365StatusDto {
  state: Microsoft365State;
  /** the person may connect or use it now (not_connected, needs_reconnect, consent_required, connected) */
  available: boolean;
  features: Record<Microsoft365Feature, boolean>;
  signInMethod: Microsoft365SignInMethod;
  account: Microsoft365AccountDto | null;
  /** the person's own on/off switch (Bağlantılar); off keeps the account */
  enabled: boolean;
  /** off_team: the team whose admin turned it off */
  teamName: string | null;
  /** the person can open Yönetim › Entegrasyonlar › Microsoft 365 */
  canManage: boolean;
  /**
   * part 3: per feature, the account's granted scopes cover it (hasFeatureScopes); false without an account. A feature
   * that is on but not granted shows "Yeni izin gerekiyor · yeniden bağlan".
   */
  grants: Record<Microsoft365Feature, boolean>;
}

export const microsoft365ConnectBody = z.object({
  /**
   * where the browser lands after Microsoft: a path of this app (/connectors, /c/<id>…). Phase 15g: `app`, the iPhone
   * app (with its bearer token only): Microsoft's page opens in the system's sign-in sheet and comes back to the app
   * (MICROSOFT365_APP_RETURN), which completes the sign-in itself (`POST /api/microsoft365/connect/complete`).
   */
  returnTo: z
    .union([
      z.literal('app'),
      z
        .string()
        .max(500)
        .regex(/^\/[^/\\]/),
    ])
    .default('/connectors'),
  /**
   * part 3, an upgrade sign-in: the features whose scopes Microsoft should ask for besides the read scopes. The server
   * maps them to scopes (MICROSOFT365_FEATURE_SCOPES) and refuses a feature the firm has off; never scope strings.
   */
  features: z.array(microsoft365Feature).max(6).optional(),
});

export type Microsoft365ConnectDto =
  | { kind: 'redirect'; url: string }
  | { kind: 'device'; id: string; userCode: string; verificationUri: string; expiresAt: string; interval: number };

export type Microsoft365DeviceStatus = 'pending' | 'connected' | 'expired' | 'declined' | 'failed';

export interface Microsoft365DevicePollDto {
  status: Microsoft365DeviceStatus;
  /** failed: why (the same codes as the redirect's reason) */
  reason?: Microsoft365SignInError;
  account?: Microsoft365AccountDto;
}

/**
 * Phase 15g: where a sign-in the app started comes back to. The system's sign-in sheet (ASWebAuthenticationSession)
 * hands this address to the app that opened it, with a one-time token: never Microsoft's code or error text.
 */
export const MICROSOFT365_APP_RETURN = `${APP_URL_SCHEME}://microsoft365`;

/** Phase 15g: the app completes its sign-in with the token the callback gave it (`?finish=`), with its own session. */
export const microsoft365AppCompleteBody = z.object({ finish: z.string().regex(/^[A-Za-z0-9_-]{32,128}$/) });

/** How the app's sign-in ended: connected (with the account), or failed with the same reasons as the web's redirect. */
export interface Microsoft365AppCompleteDto {
  status: 'connected' | 'failed';
  /** failed: why (Microsoft365SignInError, as `?microsoft365=error&reason=` on the web) */
  reason?: Microsoft365SignInError;
  account?: Microsoft365AccountDto;
}

export const microsoft365EnabledBody = z.object({ enabled: z.boolean() });

/** The callback's query (response_mode=query): code and state, or Microsoft's error. */
export const microsoft365CallbackQuery = z.object({
  state: z.string().min(16).max(256),
  code: z.string().min(1).max(4000).optional(),
  error: z.string().max(200).optional(),
  error_description: z.string().max(2000).optional(),
  admin_consent: z.string().max(10).optional(),
  tenant: z.string().max(100).optional(),
  session_state: z.string().max(200).optional(),
});

/** Why a sign-in failed, in the redirect (?microsoft365=error&reason=…) and in the audit log. */
export type Microsoft365SignInError =
  | 'state_invalid'
  | 'access_denied'
  | 'consent_required'
  | 'personal_account'
  | 'other_tenant'
  | 'guest_account'
  /** the Microsoft account's address is not the person's Oppy e-mail address (D-194) */
  | 'wrong_account'
  | 'id_token_invalid'
  | 'exchange_failed'
  | 'not_available'
  | 'unavailable';

// ---- turn ends the chat shows as a card (MsDurumlar) -------------------------------------------

/**
 * `turn.completed.errorCode` values after which the chat shows a Microsoft 365 card. Part 3:
 * `microsoft_scopes_required` (the feature is on, the account has not granted its scopes yet: "Yeni izin gerekiyor ·
 * yeniden bağlan"); the same code is the API's error and the gateway's failure for missing scopes, everywhere.
 */
export const MICROSOFT365_TURN_CODES = [
  'microsoft_not_connected',
  'microsoft_reconnect',
  'microsoft_consent_required',
  'microsoft_not_available',
  'microsoft_unavailable',
  'microsoft_scopes_required',
] as const;
export type Microsoft365TurnCode = (typeof MICROSOFT365_TURN_CODES)[number];

export const isMicrosoft365TurnCode = (code: string | undefined | null): code is Microsoft365TurnCode =>
  Boolean(code) && (MICROSOFT365_TURN_CODES as readonly string[]).includes(code as string);

// ---- facts from Graph, kept in the conversation (`microsoft.items` events) -------------------

const person = z.object({ name: z.string().max(200), address: z.string().max(320) });

export const microsoft365MailFacts = z.object({
  from: person,
  subject: z.string().max(500),
  receivedAt: z.string().max(40),
  isRead: z.boolean(),
  importance: z.enum(['low', 'normal', 'high']),
  folder: z.enum(['inbox', 'sentitems', 'other']),
  toCount: z.number().int().nonnegative(),
  ccCount: z.number().int().nonnegative(),
  /** the person's own address in To, in Cc, or neither (a list, Bcc, an alias); left out without their address (v0.15e.1) */
  toMe: z.enum(['to', 'cc', 'none']).optional(),
  /** sent by a machine, by its sender (a no-reply or notification address); what a text says is only the model's hint */
  automated: z.boolean().optional(),
  /** a forward (FW:, İLT:…): a hint to the model, never a rule on the card */
  forwarded: z.boolean().optional(),
  attachments: z
    .array(z.object({ ref: z.string().max(12), name: z.string().max(300), size: z.number().int().nonnegative(), contentType: z.string().max(200) }))
    .max(50),
  /**
   * part 3: the To and Cc headers as Graph gave them (at most 50 each): a write card's recipient may be the header
   * address of a person the user named (D-223), never an address from a text; left out by older workers
   */
  to: z.array(person).max(50).optional(),
  cc: z.array(person).max(50).optional(),
});

export const microsoft365EventFacts = z.object({
  subject: z.string().max(500),
  start: z.string().max(40),
  end: z.string().max(40),
  isAllDay: z.boolean(),
  location: z.string().max(300),
  online: z.boolean(),
  /** Graph's onlineMeetingProvider (teamsForBusiness, skypeForBusiness…) when online */
  provider: z.string().max(40).optional(),
  isCancelled: z.boolean(),
  showAs: z.string().max(30),
  /**
   * it takes the person's time, as the free stretches the model is told count it: not cancelled, not declined, shown
   * as busy, away, tentative or unknown (v0.15e.1; left out by older workers)
   */
  busy: z.boolean().optional(),
  organizer: person.extend({ isYou: z.boolean() }),
  attendees: z.array(person.extend({ response: z.string().max(30) })).max(200),
});

export const microsoft365AttachmentFacts = z.object({
  messageRef: z.string().max(12),
  name: z.string().max(300),
  size: z.number().int().nonnegative(),
  contentType: z.string().max(200),
  kind: z.enum(['file', 'item', 'reference']),
  /** known once the attachment was read: pages (PDF, Word), slides, sheet names */
  pages: z.number().int().nonnegative().optional(),
  slides: z.number().int().nonnegative().optional(),
  sheets: z.array(z.string().max(100)).max(50).optional(),
});

export const microsoft365ListFacts = z.object({
  folder: z.enum(['inbox', 'sentitems', 'all']),
  /** e.g. "Gelen kutusu · 31 Ekim" */
  label: z.string().max(200),
  /** all matching mails Graph counted (or the listed ones) */
  total: z.number().int().nonnegative(),
  /** the listed mails, newest first */
  refs: z.array(z.string().max(12)).max(50),
  /** the one day the search covered, in the person's zone (v0.15e.1); left out for a search of no day or of several */
  day: z.iso.date().optional(),
});

const ref = z.string().regex(/^[mael][1-9]\d{0,4}$/);

export const microsoft365Item = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('mail'), ref, graphId: z.string().max(512), facts: microsoft365MailFacts }),
  z.object({ kind: z.literal('event'), ref, graphId: z.string().max(512), facts: microsoft365EventFacts }),
  z.object({ kind: z.literal('attachment'), ref, graphId: z.string().max(512), messageGraphId: z.string().max(512), facts: microsoft365AttachmentFacts }),
  z.object({ kind: z.literal('list'), ref, facts: microsoft365ListFacts }),
]);
export type Microsoft365Item = z.infer<typeof microsoft365Item>;
export type Microsoft365MailFacts = z.infer<typeof microsoft365MailFacts>;
export type Microsoft365EventFacts = z.infer<typeof microsoft365EventFacts>;
export type Microsoft365AttachmentFacts = z.infer<typeof microsoft365AttachmentFacts>;
export type Microsoft365ListFacts = z.infer<typeof microsoft365ListFacts>;

// ---- answer cards (resolved on the server from the facts above, D-197) ----------------------

const mailCardItem = z.object({
  ref,
  from: person,
  subject: z.string().max(500),
  receivedAt: z.string().max(40),
  isRead: z.boolean(),
  importance: z.enum(['low', 'normal', 'high']),
  attachments: z.array(z.object({ name: z.string().max(300), size: z.number().int().nonnegative(), contentType: z.string().max(200) })).max(50),
  /** the model's one-line summary; null in the "N mail daha" list */
  summary: z.string().max(300).nullable(),
});

const eventCardItem = z.object({
  ref,
  /** the number on the strip and in the list */
  n: z.number().int().positive(),
  subject: z.string().max(500),
  start: z.string().max(40),
  end: z.string().max(40),
  isAllDay: z.boolean(),
  location: z.string().max(300),
  online: z.boolean(),
  provider: z.string().max(40).optional(),
  isCancelled: z.boolean(),
  /** it takes the person's time (the facts' `busy`): what the free stretch counts; cards made before v0.15e.1 have none */
  busy: z.boolean().optional(),
  organizer: person.extend({ isYou: z.boolean() }),
  attendees: z.array(person).max(200),
});

const cardBase = {
  /** where the card goes: a character offset into the message's text (between blocks) */
  at: z.number().int().nonnegative(),
  /** the zone the assistant used; times are instants and shown in this zone */
  timeZone: z.string().max(64),
  /** the model's block as it wrote it, for the model's history (never shown) */
  block: z.string().max(20_000),
};

export const answerCard = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('mail_list'),
    ...cardBase,
    /** the model's short title; null: the screen makes one from the source */
    title: z.string().max(120).nullable(),
    /** `day`: the one day the list's search covered, which the label names (v0.15e.1) */
    source: z.object({ folder: z.enum(['inbox', 'sentitems', 'all']), label: z.string().max(200), total: z.number().int().nonnegative(), day: z.iso.date().optional() }),
    groups: z.array(z.object({ kind: z.enum(['reply', 'info']), items: z.array(mailCardItem).max(30) })).max(2),
    rest: z.object({ summary: z.string().max(200).nullable(), items: z.array(mailCardItem).max(50), more: z.number().int().nonnegative() }),
  }),
  z.object({
    kind: z.literal('calendar'),
    ...cardBase,
    days: z.array(z.object({ date: z.iso.date(), events: z.array(eventCardItem).max(60) })).min(1).max(14),
  }),
  z.object({
    kind: z.literal('attachment'),
    ...cardBase,
    attachment: z.object({
      ref,
      name: z.string().max(300),
      contentType: z.string().max(200),
      size: z.number().int().nonnegative(),
      pages: z.number().int().nonnegative().optional(),
      slides: z.number().int().nonnegative().optional(),
      sheets: z.array(z.string().max(100)).max(50).optional(),
    }),
    message: z.object({ from: person, subject: z.string().max(500), receivedAt: z.string().max(40) }),
    points: z.array(z.object({ text: z.string().max(600), pages: z.object({ from: z.number().int().positive(), to: z.number().int().positive() }).nullable() })).max(12),
    /** the mail's other attachments ("Bu mailde bir ek daha var") */
    siblings: z.array(z.object({ name: z.string().max(300), size: z.number().int().nonnegative(), contentType: z.string().max(200) })).max(20),
  }),
]);
export type AnswerCard = z.infer<typeof answerCard>;
export type MailListCard = Extract<AnswerCard, { kind: 'mail_list' }>;
export type CalendarCard = Extract<AnswerCard, { kind: 'calendar' }>;
export type AttachmentCard = Extract<AnswerCard, { kind: 'attachment' }>;
export type MailCardItem = z.infer<typeof mailCardItem>;
export type EventCardItem = z.infer<typeof eventCardItem>;

/** The fence language the model writes its card blocks in (the markdown parser allows [\w+#.-]). */
export const ANSWER_CARD_FENCE = 'oppy-kart';
